import { Form, message, TableColumnProps } from "antd";
import {
  createSubscriptionPlan,
  deleteSubscriptionPlan,
  getSubscriptionPlans,
  updateSubscriptionPlan,
} from "../../services/subscriptionPlan";
import useModal from "../../hooks/useModal";
import useFetch from "../../hooks/useFetch";
import { usePagination } from "../../hooks/usePagination";
import { ISubscriptionPlan } from "../../types/interfaces";
import { useMemo } from "react";
import DeleteButton from "../../components/general/deleteButton/deleteButton";
import EditButton from "../../components/general/editButton/editButton";
import { INTERVALS_TEXT } from "../../constants/constants";
import { ValidateForm } from "../../utils/validateForm";
import Permission from "../../components/general/permission/permission";
import { PermissionBadgeList } from "../../components/general/permissionBadge/PermissionBadge";

function useSubscriptionPlans() {
  const { onTableChange, tableParams } = usePagination();

  const memoizedPagination = useMemo(
    () => ({
      current: tableParams.pagination.current,
      pageSize: tableParams.pagination.pageSize,
      sorter: tableParams.sorter,
    }),
    [
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      tableParams.sorter,
    ],
  );

  const { data, error, loading, total, refetch, startLoading } = useFetch({
    service: getSubscriptionPlans,
    pagination: memoizedPagination,
  });

  const subscriptionPlanModal = useModal();

  const [form] = Form.useForm();

  const handleOk = async () => {
    try {
      subscriptionPlanModal.loading.start();
      const validForm = await ValidateForm(form);
      if (!validForm) {
        subscriptionPlanModal.loading.stop();
        return;
      }

      const formValues = form.getFieldsValue();
      const basePermissions: string[] = formValues.permissions || [];

      // Agregar permisos padre sin duplicar
      const permSet = new Set<string>(basePermissions);
      if (Array.from(permSet).some((p) => p.includes("ui:map"))) permSet.add("ui:map");
      if (Array.from(permSet).some((p) => p.includes("ui:newsletter"))) permSet.add("ui:newsletter");

      const payloadData = {
        ...formValues,
        active: formValues.status === "active",
        permissions: Array.from(permSet),
      };

      if (subscriptionPlanModal.editing) {
        await updateSubscriptionPlan({
          ...payloadData,
          id: subscriptionPlanModal.id,
        });
      } else {
        await createSubscriptionPlan(payloadData);
      }
      message.success(subscriptionPlanModal.editing ? "Plan actualizado correctamente" : "Plan creado correctamente");
      refetch();
      handleCancel();
    } catch (error) {
      message.error("El formulario contiene errores, por favor revisa los campos");
    } finally {
      subscriptionPlanModal.loading.stop();
    }
  };

  const handleCancel = () => {
    subscriptionPlanModal.closeModal();
    subscriptionPlanModal.setEditing(false);
    subscriptionPlanModal.setId(undefined);
    form.resetFields();
    subscriptionPlanModal.loading.stop();
  };

  const handleDelete = async (id: number) => {
    try {
      startLoading();
      await deleteSubscriptionPlan(id);
      refetch();
    } catch (error) {
      message.error(
        "Ha ocurrido un error inesperado, por favor intenta de nuevo",
      );
    }
  };

  const handleEdit = (subscriptionPlan: ISubscriptionPlan) => {
    subscriptionPlanModal.openModal();
    subscriptionPlanModal.setEditing(true);
    subscriptionPlanModal.setId(subscriptionPlan.id);
    form.setFieldsValue({
      ...subscriptionPlan,
      status: subscriptionPlan.active ? "active" : "inactive",
      permissions: subscriptionPlan.edges?.permissions?.map(
        (permission) => permission.name,
      ),
    });
  };

  const tableColumns: TableColumnProps[] = [
    {
      title: "Frecuencia",
      dataIndex: "frequency",
      key: "frequency",
      sorter: (a: any, b: any) =>
        INTERVALS_TEXT[a.frequency].localeCompare(INTERVALS_TEXT[b.frequency]),
      render: (frequency: string) => {
        return INTERVALS_TEXT[frequency];
      },
    },
    {
      title: "Nombre del plan",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      sorter: (a: any, b: any) => a.description.localeCompare(b.description),
    },
    {
      title: "Valor",
      dataIndex: "price",
      key: "price",
      sorter: (a: any, b: any) => a.price - b.price,
      render: (price: number) => {
        return `$${price}`;
      },
    },
    {
      title: "Limite de usuarios",
      dataIndex: "userLimit",
      key: "userLimit",
    },
    {
      title: "Estado",
      dataIndex: "active",
      key: "active",
      render: (active: boolean) => (
        <span style={{ 
          color: active ? '#52c41a' : '#bfbfbf',
          background: active ? '#f6ffed' : '#f5f5f5',
          border: `1px solid ${active ? '#b7eb8f' : '#d9d9d9'}`,
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {active ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      title: "Permisos",
      dataIndex: "permissions",
      key: "permissions",
      width: 320,
      render: (_: any, record: any) => {
        const permSlugs = record.edges?.permissions?.map((p: any) => p.name) || [];
        return <PermissionBadgeList permissions={permSlugs} size="small" maxVisible={4} />;
      },
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      key: "actions",
      render: (_: string, record: any) => {
        return (
          <div className="actions">
            <Permission requiredPermission={"ui:subscription-plans:delete"}>
              <DeleteButton
                id={record.id}
                handleDelete={handleDelete}
                text={"Vas a eliminar un plan de suscripción, estas seguro?"}
              />
            </Permission>
            <Permission requiredPermission={"ui:subscription-plans:update"}>
              <EditButton record={record} handleEdit={handleEdit} />
            </Permission>
          </div>
        );
      },
    },
  ];

  return {
    data,
    error,
    form,
    handleOk,
    handleCancel,
    loading,
    onTableChange,
    refetch,
    tableColumns,
    tableParams,
    total,
    subscriptionPlanModal,
  };
}

export default useSubscriptionPlans;
